import { useEffect, useState, type CSSProperties } from "react"
import { Toaster as Sonner, type ToasterProps } from "sonner"

type ResolvedTheme = "light" | "dark"

function readTheme(): ResolvedTheme {
  if (typeof document === "undefined") return "light"
  return document.documentElement.classList.contains("dark") ? "dark" : "light"
}

/** 浅色页上默认 pastel 几乎发白；改为实色底，保证对比度。 */
const LIGHT_TOAST_VARS = {
  "--normal-bg": "oklch(0.22 0.01 286)",
  "--normal-text": "oklch(0.98 0 0)",
  "--normal-border": "transparent",
  "--success-bg": "oklch(0.42 0.12 150)",
  "--success-border": "oklch(0.38 0.11 150)",
  "--success-text": "oklch(0.98 0.02 150)",
  "--info-bg": "oklch(0.4 0.1 250)",
  "--info-border": "oklch(0.36 0.09 250)",
  "--info-text": "oklch(0.97 0.02 250)",
  "--warning-bg": "oklch(0.55 0.14 70)",
  "--warning-border": "oklch(0.5 0.13 70)",
  "--warning-text": "oklch(0.98 0.02 90)",
  "--error-bg": "oklch(0.5 0.18 25)",
  "--error-border": "oklch(0.45 0.17 25)",
  "--error-text": "oklch(0.98 0.02 25)",
  "--border-radius": "var(--radius)",
} as CSSProperties

const DARK_TOAST_VARS = {
  "--normal-bg": "oklch(0.28 0.01 286)",
  "--normal-text": "oklch(0.98 0 0)",
  "--normal-border": "oklch(1 0 0 / 12%)",
  "--success-bg": "oklch(0.35 0.1 150)",
  "--success-border": "oklch(0.42 0.12 150)",
  "--success-text": "oklch(0.9 0.06 150)",
  "--info-bg": "oklch(0.32 0.08 250)",
  "--info-border": "oklch(0.4 0.1 250)",
  "--info-text": "oklch(0.88 0.05 250)",
  "--warning-bg": "oklch(0.4 0.1 70)",
  "--warning-border": "oklch(0.48 0.12 70)",
  "--warning-text": "oklch(0.92 0.06 90)",
  "--error-bg": "oklch(0.38 0.14 25)",
  "--error-border": "oklch(0.46 0.16 25)",
  "--error-text": "oklch(0.92 0.05 25)",
  "--border-radius": "var(--radius)",
} as CSSProperties

function Toaster(props: ToasterProps) {
  const [theme, setTheme] = useState<ResolvedTheme>(readTheme)

  useEffect(() => {
    const sync = () => setTheme(readTheme())
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })
    return () => observer.disconnect()
  }, [])

  return (
    <Sonner
      theme={theme}
      position="top-center"
      richColors
      closeButton
      className="toaster group"
      style={theme === "dark" ? DARK_TOAST_VARS : LIGHT_TOAST_VARS}
      toastOptions={{
        classNames: {
          toast: "font-sans border shadow-lg",
          description: "opacity-90",
          closeButton: "border-0 bg-transparent opacity-70 hover:opacity-100",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
